import type { Enquiry, PublicSnapshot, SiteData } from './types';

const APP_SNAPSHOT_URL = '/api/public/app-snapshot';

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: 'include', ...init, headers: { accept: 'application/json', ...(init?.headers || {}) } });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    let message = '';
    try { message = String((JSON.parse(body) as { error?: string; detail?: string }).error || (JSON.parse(body) as { detail?: string }).detail || ''); } catch { /* Not JSON. */ }
    if (!message && (body.includes('Error 1102') || body.includes('exceeded resource limits'))) message = 'The website save was too large. Images are now uploaded separately; reload the manager and try again.';
    if (!message && response.status === 401) message = 'Your Club Manager session has expired. Please sign in again.';
    if (!message) message = `The website could not complete that request (${response.status}).`;
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export const api = {
  site: () => request<SiteData>('/api/public/site-content', { cache: 'no-store' }),
  snapshot: () => request<PublicSnapshot>(APP_SNAPSHOT_URL, { cache: 'no-store', credentials: 'omit' }),
  enquire: (body: Record<string, unknown>) => request<{ success: boolean }>('/api/public/enquiries', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  login: (password: string) => request<Record<string, unknown>>('/api/admin/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ password }) }),
  save: (data: SiteData) => request<Record<string, unknown>>('/api/admin/site-content', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(data) }),
  uploadImage: async (file: Blob, kind: 'gallery'|'sponsor'|'profile'|'website', filename = 'website-image.webp') => {
    const form = new FormData();
    form.set('file', file, filename);
    form.set('kind', kind);
    return request<{ url: string }>('/api/admin/media/upload', { method: 'POST', body: form });
  },
  enquiries: () => request<{ enquiries: Enquiry[] }>('/api/admin/enquiries'),
  updateEnquiry: (id: string, changes: Partial<Enquiry>) => request<{ success: boolean }>('/api/admin/enquiries', { method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id, ...changes }) }),
  deleteEnquiry: (id: string) => request<{ success: boolean }>('/api/admin/enquiries', { method: 'DELETE', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id }) }),
  uploadPractice: async (form: FormData) => {
    const response = await fetch('/api/admin/practice-resources/upload', { method: 'POST', credentials: 'include', body: form });
    if (!response.ok) throw new Error(await response.text());
    return response.json() as Promise<{ resource: SiteData['practiceResources'][number] }>;
  },
  deletePractice: (id: string) => request<{ success: boolean }>(`/api/admin/practice-resources/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
